'use client'

import { Button } from '@heroui/button'
import { Modal, ModalBody, ModalContent } from '@heroui/modal'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'

import { InsightsIcon } from '@/components/shared'
import { Typography } from '@/components/ui'
import { useApi } from '@/trpc/react'
import { cn } from '@/utils'

type GenerateTranscriptModalProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (transcript: string) => void
}

const MAX_FILE_SIZE = 4 * 1024 * 1024 // 4MB

const GenerateTranscriptModal = ({
  isOpen,
  onOpenChange,
  onSuccess,
}: GenerateTranscriptModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const api = useApi()

  const {
    mutate: _generateTranscript,
    isPending,
    isSuccess,
    isError,
    reset,
  } = useMutation(
    api.ai.generateTranscript.mutationOptions({
      onSuccess: (data) => {
        onSuccess(data)
      },
      onError: (error) => {
        console.error(error)
      },
    }),
  )

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.size < MAX_FILE_SIZE) {
      const reader = new FileReader()
      reader.onload = () => {
        // The result is a base64 string like "data:image/jpeg;base64,/9j/4AAQSkZJRgABA..."
        // We need to extract just the base64 part after the comma
        const base64String = (reader.result as string).split(',')[1]
        _generateTranscript({ image: base64String })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleContinue = () => {
    if (isSuccess || isError) {
      reset()
      return onOpenChange(false)
    }
    fileInputRef.current?.click()
  }

  const buttonLabel = isPending
    ? ''
    : isSuccess
      ? 'Continue'
      : isError
        ? 'Try again later'
        : 'Continue'

  const title = isPending
    ? 'Scanning...'
    : isSuccess
      ? 'Scan successful '
      : isError
        ? 'Error scanning'
        : 'Scan your handwritten entries'

  const description = isPending
    ? 'Please wait, processing image'
    : isSuccess
      ? 'Your handwritten entry has been added'
      : isError
        ? `Sorry, we couldn't process your image. Make sure the handwritten is clear and readable, or try again later`
        : 'Capture your thoughts by scanning your handwritten entries. You can take a photo or choose one from your image gallery to add it in Moments'

  return (
    <Modal
      placement="center"
      size="sm"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      classNames={{
        base: 'mx-6',
        body: 'items-center gap-4 py-8 px-6',
      }}
    >
      <ModalContent>
        {() => (
          <ModalBody>
            <div className="flex flex-col items-center">
              <InsightsIcon
                className={cn(
                  'mb-3 text-[56px] text-default-foreground',
                  isSuccess && 'text-success',
                  isError && 'text-danger',
                )}
              />
              <Typography
                size="2xlarge"
                weight="regular"
                className="mb-4 max-w-[211px] text-center font-crimsonPro leading-6 text-foreground"
              >
                {title}
              </Typography>
              <Typography
                size="xsmall"
                className="text-center text-content4-foreground"
                weight="regular"
              >
                {description}
              </Typography>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
              multiple
            />
            <Button
              size="lg"
              variant="faded"
              color="primary"
              isLoading={isPending}
              fullWidth
              className="rounded-[80px]"
              onPress={handleContinue}
            >
              {buttonLabel}
            </Button>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  )
}

export default GenerateTranscriptModal
